/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useEffect, useState } from "react";
import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import { useTranslation } from "react-i18next";
import MPHeader from "./partials/MPHeader";
import SOPTargetSpecification from "./partials/SOPTargetSpecification";
import MediaPowderSelectionBox from "./partials/MediaPowderSelectionBox";
import MediaSolutionSelectionBox from "./partials/MediaSolutionSelectionBox";
import DeviationAndSopGuidelines from "./partials/DeviationAndSopGuidelines";
import MPFooter from "./partials/MPFooter";
import MediaProcessParameters from "./partials/MediaProcessParameters";
import type {
	MediaPowderBatches,
	MediaPowderBatchInfo,
	MediaSolutionBatches,
	PowderLotDetail,
	SolutionLotDetail,
	SolutionPowderMixingPayload,
	ValidationErrors
} from "../../types/mediaBatchPreparation.types";
import CustomToast from "../../../../../widgets/toaster";
import OptionalAdditives from "../../common/OptionalAddictives";
import { useMediaBatchPrepView } from "../../../../../hooks/useMediaBatchPrepView";

interface Props {
	onNext: () => void;
	onBack: () => void;
	batchId: number | null;
}
const MediaBatchPreparation = ({ onNext, onBack, batchId }: Props) => {
	const { t } = useTranslation();
	const goNext = () => {
		onNext();
	};

	const goBack = () => {
		onBack();
	};

	const [errors, setErrors] = useState<ValidationErrors>({
		powderBatch: "",
		powderLot: "",
		powderQuantity: "",
		solutionBatch: "",
		solutionLot: "",
		startTime: "",
		endTime: "",
		observedPh: "",
		visualStandards: "",
		additiveQuantity: "",
	});

	const validateFields = () => {
	const newErrors = {
		powderBatch: "",
		powderLot: "",
		powderQuantity: "",
		solutionBatch: "",
		solutionLot: "",
		startTime: "",
		endTime: "",
		observedPh: "",
		visualStandards: "",
		additiveQuantity: "",
	};

	let valid = true;
	if (!selectedPowderBatch) {
		newErrors.powderBatch = t("mediaBatchInit.errors.powderBatch");
		valid = false;
	}

	if (!selectedPowderBatchLot) {
		newErrors.powderLot = t("mediaBatchInit.errors.powderLot");
		valid = false;
	}

	if (!mixingParameters.powder_quantity_consumed) {
		newErrors.powderQuantity = t("mediaBatchInit.errors.powderQty");
		valid = false;
	}

	if (!selectedSolutionBatch) {
		newErrors.solutionBatch = t("mediaBatchInit.errors.solutionBatch");
		valid = false;
	}

	if (!selectedSolutionBatchLot) {
		newErrors.solutionLot = t("mediaBatchInit.errors.solutionLot");
		valid = false;
	}

	if (!mixingParameters.process_start_time) {
		newErrors.startTime = t("mediaBatchInit.errors.startTime");
		valid = false;
	}

	if (!mixingParameters.process_end_time) {
		newErrors.endTime = t("mediaBatchInit.errors.endTime");
		valid = false;
	}

	if (!mixingParameters.observed_ph) {
		newErrors.observedPh = t("mediaBatchInit.errors.observedPh");
		valid = false;
	}
	const hasInvalidAdditive = [
	...mixingParameters.additives,
	...mixingParameters.reagents,
	].some((item) => !item.quantity || Number(item.quantity) <= 0);

	if (hasInvalidAdditive) {
		newErrors.additiveQuantity = t("mediaBatchInit.errors.additiveQuantity");
		valid = false;
	}

	setErrors(newErrors);
	return valid;
	};

	const { mediaBatchData, fetchBatchDetails} = useMediaBatchPrepView();
	const [mixingParameters, setMixingParameters] =
		useState<SolutionPowderMixingPayload>({
			media_batch_id: "",
			media_master_id: "",
			intended_use_id: "",
			powder_batch_id: "",
			powder_lot_id: "",
			powder_quantity_consumed: "",
			solution_batch_id: "",
			solution_lot_id: "",
			solution_volume_used: "",
			process_start_time: "",
			process_end_time: "",
			stirring_duration_minutes: "",
			agitation_rpm: "",
			observed_ph: "",
			process_remarks: "",
			has_deviation: 0,
			deviation_remarks: "",
			additives: [],
			reagents: [],
		});
	const [selectedPowderBatch, setSelectedPowderBatch] =
		useState<MediaPowderBatches | null>(null);
	const [selectedPowderBatchLot, setSelectedPowderBatchLot] =
		useState<PowderLotDetail | null>(null);

	const [selectedSolutionBatch, setSelectedSolutionBatch] =
		useState<MediaSolutionBatches | null>(null);
	const [selectedSolutionBatchLot, setSelectedSolutionBatchLot] =
		useState<SolutionLotDetail | null>(null);

	const [batchInfo, setBatchInfo] = useState<MediaPowderBatchInfo | null>(
		null,
	);
	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	useEffect(() => {
		if (batchId) {
			fetchBatchDetails(batchId);
		}
	}, []);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaBatchInit.title")} />
			<div className="flex flex-col bg-[#F8FAFC]">
				<MPHeader goBack={goBack} mediaBatchData={mediaBatchData} />
				<div className="p-6 space-y-6">
					{/* <SOPTargetSpecification /> */}
					<MediaPowderSelectionBox
						powderConsumption={mediaBatchData?.powder_consumption[0]}
						setSelectedPowderBatch={setSelectedPowderBatch}
						selectedPowderBatch={selectedPowderBatch}
						setSelectedPowderBatchLot={setSelectedPowderBatchLot}
						selectedPowderBatchLot={selectedPowderBatchLot}
						mixingParameters={mixingParameters}
						setMixingParameters={setMixingParameters}
						sopRatio={mediaBatchData?.sop_mixing_ratio}
						setBatchInfo={setBatchInfo}
						errors={errors}
						setErrors={setErrors}
					/>
					<MediaSolutionSelectionBox
						solutionConsumption={mediaBatchData?.solution_consumption[0]}
						selectedSolutionBatch={selectedSolutionBatch}
						selectedSolutionBatchLot={selectedSolutionBatchLot}
						setSelectedSolutionBatch={setSelectedSolutionBatch}
						setSelectedSolutionBatchLot={setSelectedSolutionBatchLot}
						mixingParameters={mixingParameters}
						setMixingParameters={setMixingParameters}
						errors={errors}
						setErrors={setErrors}
					/>
					<OptionalAdditives
						onChange={({ additives, reagents }) =>
							setMixingParameters((prev) => ({
								...prev,
								additives,
								reagents,
							}))
						}
						errors={errors}
						setErrors={setErrors}
					/>
					<MediaProcessParameters
						mediaPreparationData={mediaBatchData?.media_preparation}
						mixingParameters={mixingParameters}
						setMixingParameters={setMixingParameters}
						batchInfo={batchInfo}
						errors={errors}
						setErrors={setErrors}
					/>
					<DeviationAndSopGuidelines />
				</div>
				<MPFooter
					selectedPowderBatch={selectedPowderBatch}
					selectedSolutionBatch={selectedSolutionBatch}
					selectedPowderLot={selectedPowderBatchLot}
					selectedSolutionLot={selectedSolutionBatchLot}
					showToast={showToast}
					mixingParameters={mixingParameters}
					mediaBatchData={mediaBatchData}
					goNext={goNext}
					goBack={goBack}
					validateFields={validateFields}
				/>
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={2500}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight" // optional
			/>
		</div>
	);
};

export default MediaBatchPreparation;